import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../../api/client';
import '../quotes/quote-generator.css';

type CalendarTechnician = { _id: string; name: string };

type CalendarWorkOrder = {
  _id: string;
  title?: string;
  status?: string;
  technicianName?: string;
  assignedTechId?: string | { _id?: string };
  client?: { name?: string; address?: string };
  schedule?: { date?: string; startTime?: string; endTime?: string };
};

type CalendarPayload = {
  period: { from: string; to: string };
  technicians: CalendarTechnician[];
  workOrders: CalendarWorkOrder[];
};

function startOfWeek(date: Date): Date {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

function dayKey(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function assignedTechId(wo: CalendarWorkOrder): string | null {
  const raw = wo.assignedTechId;
  if (!raw) return null;
  if (typeof raw === 'string') return raw;
  return raw._id ? String(raw._id) : null;
}

function statusClass(status?: string): string {
  const s = (status || '').toLowerCase();
  if (s === 'draft') return 'job-cal-block job-cal-block--draft';
  if (s === 'scheduled') return 'job-cal-block job-cal-block--scheduled';
  if (s === 'in-progress') return 'job-cal-block job-cal-block--progress';
  if (s === 'completed') return 'job-cal-block job-cal-block--done';
  return 'job-cal-block';
}

export default function WorkOrdersCalendar(): React.JSX.Element {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [payload, setPayload] = useState<CalendarPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [techFilter, setTechFilter] = useState<string>('all');

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const from = dayKey(weekStart);
      const to = dayKey(addDays(weekStart, 6));
      const techQuery = techFilter !== 'all' ? `&technicianId=${encodeURIComponent(techFilter)}` : '';
      const res = await apiRequest<{ data: CalendarPayload }>(
        `/api/work-orders/calendar?from=${from}&to=${to}${techQuery}`
      );
      setPayload(res.data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message.replace(/^HTTP \d+[^:]+:\s*/, '') : 'Failed to load calendar.');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [weekStart, techFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const jobsByTechDay = useMemo(() => {
    const map = new Map<string, CalendarWorkOrder[]>();
    for (const wo of payload?.workOrders || []) {
      if (!wo.schedule?.date) continue;
      const date = new Date(wo.schedule.date);
      if (Number.isNaN(date.getTime())) continue;
      const techId = assignedTechId(wo) || 'unassigned';
      const properKey = `${techId}:${dayKey(date)}`;
      const list = map.get(properKey) || [];
      list.push(wo);
      map.set(properKey, list);
    }
    return map;
  }, [payload]);

  const technicians = payload?.technicians || [];
  const rows =
    techFilter === 'all'
      ? [...technicians, { _id: 'unassigned', name: 'Unassigned / draft' }]
      : technicians.filter((t) => t._id === techFilter);

  return (
    <div className="job-calendar">
      <div className="job-calendar-toolbar">
        <div className="job-calendar-nav">
          <button type="button" className="tech-btn tech-btn-ghost" onClick={() => setWeekStart((d) => addDays(d, -7))}>
            ← Prev
          </button>
          <span>
            {weekDays[0].toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} –{' '}
            {weekDays[6].toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
          <button type="button" className="tech-btn tech-btn-ghost" onClick={() => setWeekStart((d) => addDays(d, 7))}>
            Next →
          </button>
        </div>
        <label className="job-calendar-filter">
          <span>Technician</span>
          <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)}>
            <option value="all">All</option>
            {technicians.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? <div className="banner banner-error">{error}</div> : null}

      {loading ? (
        <p className="tech-sub">Loading schedule board…</p>
      ) : (
        <div className="job-calendar-scroll">
          <table className="job-calendar-table">
            <thead>
              <tr>
                <th className="job-calendar-tech-col">Technician</th>
                {weekDays.map((day) => (
                  <th key={dayKey(day)}>
                    <div>{day.toLocaleDateString(undefined, { weekday: 'short' })}</div>
                    <div className="job-calendar-day-num">{day.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((tech) => (
                <tr key={tech._id}>
                  <th scope="row" className="job-calendar-tech-col">
                    {tech.name}
                  </th>
                  {weekDays.map((day) => {
                    const key = `${tech._id}:${dayKey(day)}`;
                    const jobs = jobsByTechDay.get(key) || [];
                    return (
                      <td key={key} className="job-calendar-cell">
                        {jobs.map((job) => (
                          <button
                            key={job._id}
                            type="button"
                            className={statusClass(job.status)}
                            onClick={() => navigate(`/admin/work-order/new?id=${job._id}`)}
                            title={job.client?.address || ''}
                          >
                            <span className="job-cal-block-title">{job.title || 'Job'}</span>
                            <span className="job-cal-block-meta">
                              {(job.status || 'draft').replace(/-/g, ' ')}
                              {job.schedule?.startTime ? ` · ${job.schedule.startTime}` : ''}
                            </span>
                            <span className="job-cal-block-site">{job.client?.name || '—'}</span>
                          </button>
                        ))}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="job-calendar-legend">
        <span className="job-cal-block job-cal-block--draft">Draft</span>
        <span className="job-cal-block job-cal-block--scheduled">Scheduled</span>
        <span className="job-cal-block job-cal-block--progress">In progress</span>
        <span className="job-cal-block job-cal-block--done">Completed</span>
      </div>
    </div>
  );
}
